/* ===========================================================
   Plano único com faixas por faturamento (decisions.md §20).
   Seleção pura da faixa a partir do faturamento mensal médio
   (média móvel de 90 dias) — a régua da cobrança.
   =========================================================== */
import { brl } from "./format";

/** Estrutural — `FaixaPlano` de api/types.ts satisfaz. */
export interface FaixaFaturamento {
  /** Piso inclusivo (R$/mês); a primeira faixa começa em 0. */
  faturamentoMin: number;
  /** Teto exclusivo (R$/mês); null = última faixa, sem teto. */
  faturamentoMax: number | null;
}

/** Faixa onde o faturamento cai: min ≤ fat < max (última sem teto).
 *  Pressupõe faixas contíguas e crescentes — invariante do catálogo,
 *  conferida em koloni-skateshop.test.ts. */
export function faixaParaFaturamento<T extends FaixaFaturamento>(
  faixas: T[],
  faturamentoMensal: number,
): T {
  const f = faixas.find(
    (fx) =>
      faturamentoMensal >= fx.faturamentoMin &&
      (fx.faturamentoMax == null || faturamentoMensal < fx.faturamentoMax),
  );
  // Com faixas contíguas desde 0 sempre há match; o fallback cobre entrada inválida.
  const primeira = faixas[0];
  if (primeira == null) throw new Error("faixaParaFaturamento: catálogo de faixas vazio");
  return f ?? primeira;
}

/** Rótulo da faixa para exibição — "até R$ 25.000/mês". */
export function descreveFaixa(f: FaixaFaturamento): string {
  if (f.faturamentoMin <= 0 && f.faturamentoMax != null) return `até ${brl(f.faturamentoMax)}/mês`;
  if (f.faturamentoMax == null) return `acima de ${brl(f.faturamentoMin)}/mês`;
  return `${brl(f.faturamentoMin)} a ${brl(f.faturamentoMax)}/mês`;
}

/** Quantas mensalidades inteiras uma economia mensal cobre — o claim de ROI
 *  da landing ("X× a mensalidade"). Preço inválido (≤ 0) → null, nunca um
 *  número inventado. */
export function multiplosDaMensalidade(economiaMes: number, precoMensal: number): number | null {
  if (precoMensal <= 0) return null;
  return Math.floor(economiaMes / precoMensal);
}
